import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CustomSettingControllerService } from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { httpErrorStatusCodes, isDefined } from '@stillmandigital/common';
import { mergeMap, pipe, switchMap } from 'rxjs';
import { CustomSettings } from './custom-settings.interfaces';

const CUSTOM_SETTINGS_KEY = 'CLIENT_UI_CUSTOM_SETTINGS';

export const useCustomSettings = () =>
  signalStoreFeature(
    {
      props: {
        _injector: type<Injector>(),
        _dialogService: type<MatDialog>(),
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      customSettings: undefined as CustomSettings | undefined,
    }),
    withProps(() => ({
      _customSettingService: inject(CustomSettingControllerService),
    })),
    withMethods(store => {
      const openCustomSettingsDialog = async () => {
        const { CustomSettingsDialogComponent } =
          await import('app/dashboard/components/custom-settings-dialog/custom-settings-dialog.component');

        store._dialogService.open(CustomSettingsDialogComponent, {
          disableClose: true,
          injector: store._injector,
        });
      };

      const loadCustomSettings = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._customSettingService.getSettingByKey(CUSTOM_SETTINGS_KEY).pipe(
              tapResponse({
                next: setting => {
                  const customSettings = isDefined(setting.value)
                    ? (JSON.parse(setting.value) as CustomSettings)
                    : undefined;

                  patchState(store, { customSettings });

                  if (!customSettings) {
                    openCustomSettingsDialog();
                  }
                },
                error: (error: HttpErrorResponse) => {
                  // A missing setting is the first-run case, not an error.
                  if (error.status === httpErrorStatusCodes.notFound) {
                    patchState(store, { customSettings: undefined });
                    openCustomSettingsDialog();

                    return;
                  }

                  store._notificationService.showErrorNotification(
                    'CUSTOM_SETTINGS.NOTIFICATION.LOAD_CUSTOM_SETTINGS_FAILURE',
                    error,
                  );
                },
              }),
            ),
          ),
        ),
      );

      const updateCustomSettings = rxMethod<CustomSettings>(
        pipe(
          mergeMap(customSettings => {
            patchState(store, { customSettings });

            return store._customSettingService
              .updateSetting({
                key: CUSTOM_SETTINGS_KEY,
                value: JSON.stringify(customSettings),
              })
              .pipe(
                tapResponse({
                  next: () =>
                    store._notificationService.showSuccessNotification(
                      'CUSTOM_SETTINGS.NOTIFICATION.UPDATE_CUSTOM_SETTINGS_SUCCESS',
                    ),
                  error: (error: HttpErrorResponse) =>
                    store._notificationService.showErrorNotification(
                      'CUSTOM_SETTINGS.NOTIFICATION.UPDATE_CUSTOM_SETTINGS_FAILURE',
                      error,
                    ),
                }),
              );
          }),
        ),
      );

      return { openCustomSettingsDialog, loadCustomSettings, updateCustomSettings };
    }),
  );
